import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send, Loader2, ArrowRight } from 'lucide-react';
import { publicApi } from '@/services/api';
import { toast } from 'sonner';

const ContactoV2 = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Al llegar con #contacto-form (ej. desde el CTA "Solicitar Cotización"), baja al formulario
  useEffect(() => {
    if (location.hash === '#contacto-form') {
      const el = document.getElementById('contacto-form');
      // esperar a que ScrollToTop y el render terminen
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [location]);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    asunto: '',
    mensaje: ''
  });

  const sucursales = [
    {
      nombre: 'Matriz',
      direccion: 'Carr. Tepic-Guadalajara #5251, Camichín de Jauja, C.P. 63508, Tepic, Nayarit. México.',
      telefono: '311 129 2042 y 311 129 2043'
    },
    {
      nombre: 'Sucursal Independencia',
      direccion: 'Independencia #367, Col. Los Llanitos, C.P. 63170, Tepic, Nayarit. México.',
      telefono: '311 223 2624'
    },
    {
      nombre: 'Sucursal Acaponeta',
      direccion: 'Boulevard Morelos #122-Pte, Col. Centro, Acaponeta, Nayarit.',
      telefono: '325 688 1137'
    }
  ];

  const validateForm = () => {
    const newErrors = {};
    if (!formData.nombre.trim()) newErrors.nombre = 'El nombre es requerido';
    if (!formData.email.trim()) newErrors.email = 'El email es requerido';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Ingresa un email válido';
    if (!formData.mensaje.trim()) newErrors.mensaje = 'El mensaje es requerido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) setErrors({ ...errors, [field]: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      await publicApi.createContact(formData);
      toast.success('Mensaje enviado exitosamente');
      setFormData({
        nombre: '',
        email: '',
        telefono: '',
        asunto: '',
        mensaje: ''
      });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Error al enviar el mensaje';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Error al enviar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-v2-darker min-h-screen">
      {/* Hero Banner */}
      <div className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?w=1920"
            alt="Contacto"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-v2-darker/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-12 h-0.5 bg-v2-orange" />
            <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">Contáctanos</span>
            <span className="w-12 h-0.5 bg-v2-orange" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Ponte en <span className="text-v2-orange">Contacto</span>
          </h1>
          <p className="text-xl text-v2-text-muted max-w-2xl mx-auto">
            Estamos para atenderte. Contáctanos y con gusto te asesoraremos
          </p>
        </div>
      </div>

      {/* Contact Info Cards */}
      <section className="py-12 bg-v2-dark border-b border-v2-border">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-v2-gray border border-v2-border p-6 text-center group hover:border-v2-orange transition-colors">
              <div className="w-14 h-14 bg-v2-orange/10 group-hover:bg-v2-orange rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                <Phone className="text-v2-orange group-hover:text-white" size={24} />
              </div>
              <h3 className="text-white font-bold mb-1">Teléfono</h3>
              <a href="tel:3111292042" className="text-v2-text-muted hover:text-v2-orange transition-colors block">311 129 2042</a>
              <a href="tel:3111292043" className="text-v2-text-muted hover:text-v2-orange transition-colors block">311 129 2043</a>
            </div>
            
            <div className="bg-v2-gray border border-v2-border p-6 text-center group hover:border-v2-orange transition-colors">
              <div className="w-14 h-14 bg-v2-orange/10 group-hover:bg-v2-orange rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                <Mail className="text-v2-orange group-hover:text-white" size={24} />
              </div>
              <h3 className="text-white font-bold mb-1">Email</h3>
              <a href="mailto:ventas@nayaceros.com" className="text-v2-text-muted hover:text-v2-orange transition-colors">
                ventas@nayaceros.com
              </a>
            </div>
            
            <div className="bg-v2-gray border border-v2-border p-6 text-center group hover:border-v2-orange transition-colors">
              <div className="w-14 h-14 bg-v2-orange/10 group-hover:bg-v2-orange rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                <MapPin className="text-v2-orange group-hover:text-white" size={24} />
              </div>
              <h3 className="text-white font-bold mb-1">Ubicación</h3>
              <a
                href="https://maps.app.goo.gl/TT39HBZrVGHVWXw29"
                target="_blank"
                rel="noopener noreferrer"
                className="text-v2-text-muted hover:text-v2-orange transition-colors"
              >
                Tepic, Nayarit, México
              </a>
            </div>
            
            <div className="bg-v2-gray border border-v2-border p-6 text-center group hover:border-v2-orange transition-colors">
              <div className="w-14 h-14 bg-v2-orange/10 group-hover:bg-v2-orange rounded-full flex items-center justify-center mx-auto mb-4 transition-colors">
                <Clock className="text-v2-orange group-hover:text-white" size={24} />
              </div>
              <h3 className="text-white font-bold mb-1">Horario</h3>
              <p className="text-v2-text-muted text-sm">Lun-Vie: 8:00 AM - 18:00 PM</p>
              <p className="text-v2-text-muted text-sm">Sáb: 8:00 AM - 13:30 PM</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Form */}
            <div id="contacto-form" className="scroll-mt-24 bg-v2-gray border border-v2-border p-8 lg:p-10">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-12 h-0.5 bg-v2-orange" />
                <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">Escríbenos</span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-8">
                Envía un <span className="text-v2-orange">Mensaje</span>
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form-v2" noValidate>
                <div>
                  <input
                    type="text"
                    placeholder="Tu nombre completo *"
                    value={formData.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    className={`w-full px-5 py-4 bg-v2-dark border text-white placeholder-v2-text-muted focus:border-v2-orange focus:outline-none transition-colors ${errors.nombre ? 'border-red-500' : 'border-v2-border'}`}
                    data-testid="contact-nombre-v2"
                  />
                  {errors.nombre && <p className="text-red-400 text-xs mt-1" data-testid="error-nombre-v2">{errors.nombre}</p>}
                </div>
                
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <input
                      type="email"
                      placeholder="Tu email *"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      className={`w-full px-5 py-4 bg-v2-dark border text-white placeholder-v2-text-muted focus:border-v2-orange focus:outline-none transition-colors ${errors.email ? 'border-red-500' : 'border-v2-border'}`}
                      data-testid="contact-email-v2"
                    />
                    {errors.email && <p className="text-red-400 text-xs mt-1" data-testid="error-email-v2">{errors.email}</p>}
                  </div>
                  <input
                    type="tel"
                    placeholder="Tu teléfono"
                    value={formData.telefono}
                    onChange={(e) => handleChange('telefono', e.target.value)}
                    className="w-full px-5 py-4 bg-v2-dark border border-v2-border text-white placeholder-v2-text-muted focus:border-v2-orange focus:outline-none transition-colors"
                    data-testid="contact-telefono-v2"
                  />
                </div>
                
                <div>
                  <input
                    type="text"
                    placeholder="Asunto"
                    value={formData.asunto}
                    onChange={(e) => handleChange('asunto', e.target.value)}
                    className="w-full px-5 py-4 bg-v2-dark border border-v2-border text-white placeholder-v2-text-muted focus:border-v2-orange focus:outline-none transition-colors"
                    data-testid="contact-asunto-v2"
                  />
                </div>
                
                <div>
                  <textarea
                    rows={5}
                    placeholder="Tu mensaje *"
                    value={formData.mensaje}
                    onChange={(e) => handleChange('mensaje', e.target.value)}
                    className={`w-full px-5 py-4 bg-v2-dark border text-white placeholder-v2-text-muted focus:border-v2-orange focus:outline-none resize-none transition-colors ${errors.mensaje ? 'border-red-500' : 'border-v2-border'}`}
                    data-testid="contact-mensaje-v2"
                  />
                  {errors.mensaje && <p className="text-red-400 text-xs mt-1" data-testid="error-mensaje-v2">{errors.mensaje}</p>}
                </div>
                
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-v2-orange hover:bg-v2-orange-dark text-white py-4 font-bold uppercase tracking-wider flex items-center justify-center gap-3 disabled:opacity-50 transition-colors"
                  style={{ clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)' }}
                  data-testid="contact-submit-v2"
                >
                  {loading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                  Enviar Mensaje
                </button>
              </form>
            </div>

            {/* Locations */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <span className="w-12 h-0.5 bg-v2-orange" />
                <span className="text-v2-orange uppercase tracking-wider text-sm font-medium">Ubicaciones</span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-8">
                Nuestras <span className="text-v2-orange">Sucursales</span>
              </h2>
              
              <div className="space-y-6">
                {sucursales.map((suc, idx) => (
                  <div 
                    key={idx} 
                    className="bg-v2-gray border border-v2-border p-6 group hover:border-v2-orange transition-colors"
                  >
                    <h3 className="text-xl font-bold text-v2-orange uppercase mb-3">{suc.nombre}</h3>
                    <div className="flex items-start gap-3 text-v2-text-muted mb-2">
                      <MapPin size={18} className="text-v2-orange flex-shrink-0 mt-1" />
                      <span>{suc.direccion}</span>
                    </div>
                    <div className="flex items-center gap-3 text-v2-text-muted">
                      <Phone size={18} className="text-v2-orange" />
                      <a href={`tel:${suc.telefono.split('/')[0].trim().replace(/\s/g, '')}`} className="hover:text-v2-orange transition-colors">
                        {suc.telefono}
                      </a>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactoV2;
